import { Link } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import type { Iso20022Message } from '@/types';
import type { Sample } from '@/types';
import { MessagePicker } from './MessagePicker';
import type { Translate } from './types';

export function GenericEditorSidebar({
  message,
  short,
  samples,
  activeSample,
  onSelectSample,
  onReset,
  t,
  hidePicker = false,
}: {
  message: Iso20022Message;
  short: string;
  samples: Sample[];
  activeSample: number;
  onSelectSample: (index: number) => void;
  onReset: () => void;
  t: Translate;
  hidePicker?: boolean;
}) {
  return (
    <aside className="panel space-y-3 p-3">
      {!hidePicker && <MessagePicker short={short} />}

      <div>
        <p className="eyebrow">{t(`area.${message.area}`)}</p>
        <p className="mt-1 text-[13px] leading-relaxed text-muted">{message.purpose}</p>
        <p className="mt-2 font-mono text-[11px] text-muted">
          {t('try.rootElement')}: <span className="text-ink">{message.rootElement}</span>
        </p>
      </div>

      {samples.length > 1 && (
        <div>
          <p className="eyebrow mb-1.5">{t('try.sampleVariant')}</p>
          <div className="flex flex-col gap-1">
            {samples.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onSelectSample(i)}
                className={`border px-2 py-1.5 text-left text-[12px] transition-colors ${
                  i === activeSample
                    ? 'border-ink bg-ink text-white'
                    : 'border-rule bg-surface hover:border-ink'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 border border-rule px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-muted hover:border-ink hover:text-ink"
        >
          <RotateCcw size={11} aria-hidden />
          {t('try.reset')}
        </button>
      </div>

      {!hidePicker && (
        <p className="text-[12px] leading-relaxed text-muted">
          <Link to={`/messages/${message.short}`} className="text-signal hover:underline">
            {t('try.catalogPage')} →
          </Link>
        </p>
      )}
    </aside>
  );
}
